/**
 * The "Decide today" queue: which pending items Grant should look at first, and
 * the four facts that let him decide one at a glance (impact, effort, risk,
 * confidence). Pure, so it is unit-tested; every field Grok Bot does not send yet
 * reads "Not stated" rather than being guessed.
 */

import { ActionItem } from './types';
import type { ActionRow } from './selectors';

export const QUEUE_SIZE = 7;

/** Waiting for Grant: decidable (blockers are not proposals) and not yet decided. */
export const isWaiting = (a: ActionItem): boolean => a.decidable && (!a.decision || a.decision.value === 'pending');

const CODE_RANK: Record<string, number> = { P0: 0, P1: 1, P2: 2 };
const execAlternative = (a: ActionItem): boolean => a.exec_review === 'ALTERNATIVE' || a.recommended_by === 'exec';

/**
 * Exec alternatives first (an exec has already disagreed with the bot, so Grant's
 * call is the tie-breaker), then P0 → P2, then score high to low, then the oldest
 * finding (it has waited longest), then id so the order never jitters.
 */
export function decideRank(x: ActionItem, y: ActionItem): number {
  const alt = Number(execAlternative(y)) - Number(execAlternative(x));
  if (alt) return alt;
  const code = (CODE_RANK[x.priority_code || ''] ?? 3) - (CODE_RANK[y.priority_code || ''] ?? 3);
  if (code) return code;
  const score = (typeof y.score === 'number' ? y.score : -1) - (typeof x.score === 'number' ? x.score : -1);
  if (score) return score;
  const dx = x.source_date || '9999';
  const dy = y.source_date || '9999';
  if (dx !== dy) return dx < dy ? -1 : 1;
  return x.id < y.id ? -1 : x.id > y.id ? 1 : 0;
}

/**
 * The ranked queue: every waiting item, plus the ones Grant decided from the
 * queue in this visit (`keep`), so a card he just answered stays in place with its
 * Undo instead of vanishing on the next poll.
 */
export function decideQueue(rows: ActionRow[], keep: ReadonlySet<string> = new Set()): ActionRow[] {
  return rows
    .filter((r) => isWaiting(r.action) || (keep.has(r.action.id) && r.action.decidable))
    .sort((a, b) => decideRank(a.action, b.action));
}

export const waitingCount = (rows: ActionRow[]): number => rows.filter((r) => isWaiting(r.action)).length;

// ---- Impact / effort / risk / confidence ------------------------------------

export interface Fact { label: 'Impact' | 'Effort' | 'Risk' | 'Confidence'; value: string | null; basis: string | null }

const range = (lo?: number | null, hi?: number | null): string | null => {
  const ok = (n?: number | null): n is number => typeof n === 'number' && Number.isFinite(n);
  if (ok(lo) && ok(hi)) return lo === hi ? fmtN(lo) : `${fmtN(lo)}–${fmtN(hi)}`;
  if (ok(lo)) return `${fmtN(lo)}+`;
  if (ok(hi)) return `up to ${fmtN(hi)}`;
  return null;
};
const fmtN = (n: number): string => (Math.abs(n) >= 1000 ? `${Math.round(n / 100) / 10}K` : String(Math.round(n * 100) / 100));

const IMPACT_UNIT: Record<string, (r: string) => string> = {
  paid_subs: (r) => `${r} paid subs`,
  conversion_pct: (r) => `${r} pts conversion`,
  mrr_usd: (r) => `$${r} MRR`,
  cost_usd: (r) => `$${r} cost`,
};

/** Impact: schema v4's quantified `impact` when present; today, the bucket and score Grok Bot already sends. */
export function impactFact(a: ActionItem): Fact {
  const v4 = a.impact;
  if (v4 && typeof v4 === 'object') {
    const r = range(v4.low, v4.high);
    const fmt = v4.metric && IMPACT_UNIT[v4.metric];
    const body = r ? (fmt ? fmt(r) : `${r}${v4.unit ? ` ${v4.unit}` : ''}`) : null;
    if (body) return { label: 'Impact', value: `${body}${v4.horizon_days ? ` / ${v4.horizon_days} d` : ''}`, basis: v4.basis || null };
  }
  const bucket = a.bucket && a.bucket !== 'Unbucketed' ? a.bucket : null;
  const score = typeof a.score === 'number' ? `${a.score}/10` : null;
  const value = [bucket, score].filter(Boolean).join(' · ') || null;
  return { label: 'Impact', value, basis: value ? 'Bucket and score from the bot files (not yet quantified)' : null };
}

export function effortFact(a: ActionItem): Fact {
  const e = a.effort;
  if (e && typeof e === 'object') {
    const r = range(e.hours_low, e.hours_high);
    if (r) return { label: 'Effort', value: `${r} h${e.who ? ` · ${e.who}` : ''}`, basis: e.basis || null };
  }
  return { label: 'Effort', value: a.effort_risk || null, basis: a.effort_risk ? 'effort_risk from the bot files' : null };
}

export function riskFact(a: ActionItem): Fact {
  const r = a.risk;
  if (r && typeof r === 'object' && r.tier) {
    const rev = r.reversible === true ? 'reversible' : r.reversible === false ? 'irreversible' : null;
    return { label: 'Risk', value: [`Tier ${r.tier}`, rev, r.blast_radius].filter(Boolean).join(' · '), basis: r.rollback ? `Rollback: ${r.rollback}` : null };
  }
  return { label: 'Risk', value: null, basis: null };
}

export function confidenceFact(a: ActionItem): Fact {
  const c = a.confidence;
  if (typeof c === 'number' && c >= 0 && c <= 1) return { label: 'Confidence', value: `${Math.round(c * 100)}%`, basis: a.confidence_basis || null };
  return { label: 'Confidence', value: null, basis: null };
}

export const facts = (a: ActionItem): Fact[] => [impactFact(a), effortFact(a), riskFact(a), confidenceFact(a)];

// ---- Today's progress -----------------------------------------------------

const ICT_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit' });
const ictDay = (t: number): string => ICT_DAY.format(new Date(t));

export interface DecideStats { decidedToday: number; inProgress: number; doneThisWeek: number }

/** Decided today (ICT calendar day), implementation in progress now, and done in the last 7 days. */
export function decideStats(rows: ActionRow[], now: number = Date.now()): DecideStats {
  const today = ictDay(now);
  const weekAgo = now - 7 * 24 * 3600 * 1000;
  let decidedToday = 0; let inProgress = 0; let doneThisWeek = 0;
  rows.forEach(({ action: a }) => {
    const d = a.decision;
    if (d && d.value !== 'pending' && d.decided_at) {
      const t = Date.parse(d.decided_at);
      if (!Number.isNaN(t) && ictDay(t) === today) decidedToday += 1;
    }
    const impl = a.implementation;
    if (impl && (impl.status === 'in_progress' || impl.status === 'queued')) inProgress += 1;
    if (impl && impl.status === 'done' && impl.updated_at) {
      const t = Date.parse(impl.updated_at);
      if (!Number.isNaN(t) && t >= weekAgo) doneThisWeek += 1;
    }
  });
  return { decidedToday, inProgress, doneThisWeek };
}
